import { ArrowRight, Bot, CalendarClock, CircleAlert, SlidersHorizontal } from '../components/icons';
import { useState } from 'react';
import { AnnualImpactTable } from '../components/AnnualImpactTable';
import { MetricCard } from '../components/MetricCard';
import { TrendChart } from '../components/TrendChart';
import { YearSelector } from '../components/YearSelector';
import { formatCurrency, formatMonth } from '../domain/format';
import { annualizedLineTotal, summarizePeriod } from '../domain/scenarioEngine';
import { useScenario } from '../state/ScenarioContext';

export const ExecutiveView = ({ onNavigate }: { onNavigate: (view: 'expenses' | 'revenue' | 'cash') => void }) => {
  const { data, inputs, result } = useScenario();
  const [selectedYear, setSelectedYear] = useState(data.asOfMonth.slice(0, 4));
  const yearMonths = result.months.filter((month) => month.month.startsWith(`${selectedYear}-`));
  const periodBaseline = summarizePeriod(yearMonths, 'baseline');
  const periodScenario = summarizePeriod(yearMonths, 'scenario');
  const activeExpenseCuts = data.expenseLines
    .filter((line) => inputs.expenseAdjustments[line.id]?.enabled)
    .map((line) => ({
      line,
      savings: annualizedLineTotal(data, line, undefined, selectedYear) - annualizedLineTotal(data, line, inputs, selectedYear),
    }))
    .sort((a, b) => b.savings - a.savings);
  const activeRevenueLevers = data.revenueLines.filter(
    (line) => inputs.revenueAdjustments[line.id]?.enabled,
  );
  const activeClientLevers = (data.clientRevenue ?? []).filter(
    (client) => inputs.clientRevenueAdjustments?.[client.id]?.enabled,
  );
  const activeRevenueLeverCount = activeRevenueLevers.length + activeClientLevers.length;
  const s2Client = (data.clientRevenue ?? []).find((client) => /s2\s*verify/i.test(client.name));
  const s2Adjustment = s2Client ? inputs.clientRevenueAdjustments?.[s2Client.id] : undefined;
  const s2YearImpact = s2Client && s2Adjustment?.enabled
    ? yearMonths
        .filter(({ kind, month }) => kind === 'forecast' && month >= s2Adjustment.startMonth)
        .reduce((sum, { month }) => sum + (s2Client.amounts[month] ?? s2Client.baselineMonthlyRevenue) * s2Adjustment.changePercent / 100, 0)
    : 0;
  const totalAr = data.arAging.reduce((sum, bucket) => sum + bucket.amount, 0);
  const pastDueAr = data.arAging
    .filter((bucket) => bucket.recoverable)
    .reduce((sum, bucket) => sum + Math.max(0, bucket.amount), 0);

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Executive outlook · 12 months</p>
          <h2>Know what changes cash before making the decision.</h2>
          <p>
            This view separates modeled performance from collections, debt, and owner distributions.
            Every scenario edit flows through monthly profit and cash.
          </p>
        </div>
        <div className="heading-controls">
          <YearSelector months={data.months} value={selectedYear} onChange={setSelectedYear} />
          <div className="as-of-chip"><CalendarClock size={16} /> Actuals through {formatMonth(data.asOfMonth)}</div>
        </div>
      </section>

      <AnnualImpactTable months={result.months} id="executive" />

      <section className="metric-grid" aria-label="Scenario summary">
        <MetricCard label={`${selectedYear} revenue`} value={periodScenario.revenue} baseline={periodBaseline.revenue} />
        <MetricCard label={`${selectedYear} expenses`} value={periodScenario.expenses} baseline={periodBaseline.expenses} inverse />
        <MetricCard label={`${selectedYear} modeled profit`} value={periodScenario.profit} baseline={periodBaseline.profit} />
        <MetricCard label={`Ending cash · Dec ${selectedYear}`} value={periodScenario.endingCash} baseline={periodBaseline.endingCash} />
      </section>

      <section className="content-grid two-one">
        <article className="panel chart-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Monthly cash outlook</p>
              <h3>Plan vs working scenario</h3>
            </div>
            {periodScenario.runwayBreachMonth ? (
              <span className="alert-pill"><CircleAlert size={14} /> Below $0 in {formatMonth(periodScenario.runwayBreachMonth)}</span>
            ) : (
              <span className="success-pill">Cash remains positive</span>
            )}
          </div>
          <TrendChart
            labels={yearMonths.map((month) => month.month)}
            series={[
              { name: 'Plan', values: yearMonths.map((month) => month.baselineEndingCash), tone: 'baseline', dashed: true },
              { name: 'Scenario', values: yearMonths.map((month) => month.scenarioEndingCash), tone: 'primary' },
            ]}
            ariaLabel="Monthly baseline and scenario cash forecast"
            zeroLine
          />
        </article>

        <aside className="panel decision-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">Decision queue</p>
              <h3>Where to focus</h3>
            </div>
          </div>
          {s2Client ? (
            <button className="decision-row" onClick={() => onNavigate('revenue')}>
              <span className="decision-icon"><CircleAlert size={17} /></span>
              <span>
                <strong>{s2Adjustment?.enabled ? 'S2 Verify departure scenario active' : `S2 Verify active · ${formatCurrency(s2Client.baselineMonthlyRevenue)}/month`}</strong>
                <small>{s2Adjustment?.enabled ? `${formatMonth(s2Adjustment.startMonth)} · ${Math.abs(s2Adjustment.changePercent)}% loss · ${formatCurrency(s2YearImpact)} in ${selectedYear}` : 'Baseline assumes S2 stays; select an exit month to stress-test the risk.'}</small>
              </span>
              <ArrowRight size={17} />
            </button>
          ) : null}
          <button className="decision-row" onClick={() => onNavigate('expenses')}>
            <span className="decision-icon"><SlidersHorizontal size={17} /></span>
            <span>
              <strong>{activeExpenseCuts.length ? `${activeExpenseCuts.length} cost cuts active` : 'Test line-item cost cuts'}</strong>
              <small>{activeExpenseCuts.length ? `${formatCurrency(periodScenario.expenseSavings)} savings in ${selectedYear}` : 'Search every expense and set an exact reduction.'}</small>
            </span>
            <ArrowRight size={17} />
          </button>
          <button className="decision-row" onClick={() => onNavigate('revenue')}>
            <span className="decision-icon"><ArrowRight size={17} /></span>
            <span>
              <strong>{activeRevenueLeverCount ? `${activeRevenueLeverCount} revenue levers active` : 'Model revenue changes'}</strong>
              <small>{activeRevenueLeverCount ? `${formatCurrency(periodScenario.incrementalRevenue)} impact in ${selectedYear} across ${activeClientLevers.length} client and ${activeRevenueLevers.length} stream changes` : 'Test client and category-level upside and downside.'}</small>
            </span>
            <ArrowRight size={17} />
          </button>
          <button className="decision-row" onClick={() => onNavigate('cash')}>
            <span className="decision-icon"><CircleAlert size={17} /></span>
            <span>
              <strong>{formatCurrency(pastDueAr)} recoverable AR</strong>
              <small>{totalAr ? `${Math.round((pastDueAr / totalAr) * 100)}% of total receivables` : 'No receivables loaded'}</small>
            </span>
            <ArrowRight size={17} />
          </button>
        </aside>
      </section>

      <section className="panel operating-posture-panel">
        <span className="decision-icon"><Bot size={18} /></span>
        <div><p className="eyebrow">Strategic operating intent</p><h3>AI-first Deverus · Nextcore-supported execution · high-level owner oversight</h3><p>This is the target operating design. The dashboard quantifies the current roster, vendors, revenue, and scenarios; Nextcore capacity, pricing, and automation savings still need to be mapped to specific expense and productivity lines before they are counted in the financial forecast.</p></div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Scenario bridge</p>
            <h3>What creates the change in profit</h3>
          </div>
        </div>
        <div className="bridge-grid">
          <div><span>{selectedYear} plan profit</span><strong>{formatCurrency(periodBaseline.profit)}</strong></div>
          <div className="bridge-plus"><span>Revenue impact</span><strong>{formatCurrency(periodScenario.incrementalRevenue)}</strong></div>
          <div className="bridge-plus"><span>Expense savings</span><strong>{formatCurrency(periodScenario.expenseSavings)}</strong></div>
          <div className="bridge-total"><span>Scenario profit</span><strong>{formatCurrency(periodScenario.profit)}</strong></div>
        </div>
      </section>
    </div>
  );
};
